/**
 * Utility functions for JHP Weather Widget presenters
 *
 * These utilities handle unit conversions, date formatting, and other
 * operations needed by the weather presenters without depending on
 * Zoomin-specific stores or utilities.
 */

import { startOfDay } from 'date-fns'
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import type { JHPWeatherUnits } from './types'

/**
 * Temperature unit labels
 */
export const temperatureUnits = (units: JHPWeatherUnits): string => {
  return units === 'imperial' ? '°F' : '°C'
}

/**
 * Wind speed unit labels
 */
export const windSpeedUnits = (units: JHPWeatherUnits): string => {
  return units === 'imperial' ? 'mph' : 'm/s'
}

/**
 * Convert millimeters to inches
 */
export const mmToInch = (mm: number): number => {
  return mm * 0.0394
}

/**
 * Format number with limited precision (for precipitation display)
 */
export const littlePrecision = (val: number): string => {
  if (val < 0.1) return ''
  if (val < 1) return val.toFixed(1)
  return Math.round(val).toString()
}

/**
 * Format time for display (e.g., "3 PM" or "15:00")
 */
export const formatTimeDisplay = (
  timestamp: number,
  timezone: string,
): [string, string] => {
  try {
    const zonedDate = utcToZonedTime(new Date(timestamp), timezone)
    const time = format(zonedDate, 'h', { timeZone: timezone })
    const period = format(zonedDate, 'a', { timeZone: timezone })
    return [time, period]
  } catch {
    return ['--', '']
  }
}

/**
 * Format date for display (e.g., "Dec 15, 2024")
 */
export const formatDateDisplay = (
  timestamp: number,
  timezone: string,
): string => {
  try {
    const zonedDate = utcToZonedTime(new Date(timestamp), timezone)
    return format(zonedDate, 'MMM d, yyyy', { timeZone: timezone })
  } catch {
    return '--'
  }
}

/**
 * Format weekday for display (e.g., "Mon")
 */
export const formatWeekdayDisplay = (
  timestamp: number,
  timezone: string,
): string => {
  try {
    const zonedDate = utcToZonedTime(new Date(timestamp), timezone)
    return format(zonedDate, 'EEE', { timeZone: timezone })
  } catch {
    return '--'
  }
}

/**
 * Format month and day for display (e.g., "Dec 15")
 */
export const formatMonthDayDisplay = (
  timestamp: number,
  timezone: string,
): string => {
  try {
    const zonedDate = utcToZonedTime(new Date(timestamp), timezone)
    return format(zonedDate, 'MMM d', { timeZone: timezone })
  } catch {
    return '--'
  }
}

/**
 * Get start of day timestamp in given timezone
 */
export const getStartOfDay = (timestamp: number, timezone: string): number => {
  try {
    const zonedDate = utcToZonedTime(new Date(timestamp), timezone)
    const startInZone = startOfDay(zonedDate)
    return zonedTimeToUtc(startInZone, timezone).getTime()
  } catch {
    return timestamp
  }
}

/**
 * Add days to a timestamp
 */
export const addDays = (timestamp: number, days: number): number => {
  return timestamp + days * 24 * 60 * 60 * 1000
}

/**
 * Check if two timestamps are on the same day in a timezone
 */
export const isSameDay = (
  timestamp1: number,
  timestamp2: number,
  timezone: string,
): boolean => {
  return (
    getStartOfDay(timestamp1, timezone) === getStartOfDay(timestamp2, timezone)
  )
}

/**
 * Check if a timestamp is today in a timezone
 */
export const isToday = (timestamp: number, timezone: string): boolean => {
  return isSameDay(timestamp, Date.now(), timezone)
}

/**
 * Check if a timestamp is tomorrow in a timezone
 */
export const isTomorrow = (timestamp: number, timezone: string): boolean => {
  return isSameDay(timestamp, addDays(Date.now(), 1), timezone)
}
